/* eslint-disable no-unused-vars */
/* eslint-disable prefer-object-spread */
/* eslint-disable import/no-unresolved */

import {
  registerWithFacebookInstantGameSignature as GBC_registerWithFacebookInstantGameSignature,
  loginWithGameBackendCredentials as GBC_loginWithGameBackendCredentials,
  getUserProfile as GBC_getUserProfile,
  getVersion as GBC_getVersion,
  getServerTimestamp as GBC_getServerTimestamp,
  // closeEvent as GBC_closeEvent,
  GBCError,
  ERROR_CODE,
} from '@sgorg/game-backend-client';

import { OMT_Utils } from './utils/OMT_Utils';
import { OMT_DataCache } from './utils/OMT_DataCache';

// timing constants for retry throttling in milliseconds
const RETRY_MIN_DELAY = 500;
const RETRY_MAX_DELAY = 30000;
const RETRY_DELAY_INCREMENT = 2000;
const AUTH_DATA_KEY = 'authData';

/**
 * class for interfacing with GB modules
 */
export class OMT_Connect {
  /**
   * constructor
   */
  constructor() {
    this._connected = false;
    this._prevAuthData = null;
    this._errorCache = new OMT_DataCache();
    this._defineSignals();
  }

  /**
   * define phaser signals
   */
  _defineSignals() {
    this.signals = {
      onGBConnected: new Phaser.Signal(),
    };
  }

  /**
   * connect to FB and GB
   */
  async init() {
    // 2 connection attempts then continue.
    this._setGBConnectionClosedListener();
    this._connected = await this._connectToGB(false, 0, 1);

    // retry connection, but dont wait for result.
    if (!this._connected) this._retryGBConnect();

    await OMT.userData.init();
  }

  /**
   * check if GB connection is established
   * @returns {boolean}
   */
  isConnected() {
    return this._connected;
  }

  /**
   * get users profile from the backend
   * @returns {Object} profile data Object
   */
  async _getGBUserProfile() {
    let gbUserProfileData;
    try {
      gbUserProfileData = await GBC_getUserProfile();
    } catch (error) {
      gbUserProfileData = null;
      OMT_Utils.stylizedLog('could not get user profile data from the game-backend', '#FFFF00');
    }
    return gbUserProfileData;
  }

  /**
   * get the retry delay
   * @param {number} multiplier
   * @returns {number} delay in milliseconds
   */
  _getRetryDelay(multiplier) {
    const delay = RETRY_MIN_DELAY + (RETRY_DELAY_INCREMENT * multiplier);
    return Math.min(delay, RETRY_MAX_DELAY);
  }

  /**
   * connect to the Game Backend
   * @param {boolean} isRetry true if a connection retry
   * @returns {boolean} connection status
   */
  async _connectToGB(forceRegister = false, retryCount = 0, maxRetries = 0) {
    let authData;
    const gameId = G.BuildEnvironment.APP_ID;
    const connected = await (async () => {
      // not a reconnection
      if (!this._prevAuthData) {
        // Fetch the FB signature key to securly login to the GB
        const data = await FBInstant.player.getDataAsync([AUTH_DATA_KEY]);
        authData = data[AUTH_DATA_KEY];

        if (!authData || forceRegister) { // If there is no stored signature key, register the user and store the key
          const playerInfo = await FBInstant.player.getSignedPlayerInfoAsync();
          const signature = playerInfo.getSignature();
          authData = await GBC_registerWithFacebookInstantGameSignature({ gameId, signature });
          await FBInstant.player.setDataAsync({ [AUTH_DATA_KEY]: authData });
        }
      } else { // use authData from previous connection
        authData = this._prevAuthData;
      }

      const loginConfig = Object.assign({ gameId }, authData);
      await GBC_loginWithGameBackendCredentials(loginConfig);
    })().then(() => {
      this._prevAuthData = authData; // store valid authData for reconnection
      OMT_Utils.stylizedLog(`OMT_Connect: Connected (GBC v${GBC_getVersion()})`);
      return true;
    }).catch(async (error) => {
      // we hit our max retry count
      if (maxRetries !== 0 && retryCount === maxRetries) {
        OMT_Utils.stylizedLog('OMT_Connect: Max retries hit');
        return false;
      }
      // delay reconnection
      await new Promise((resolve) => setTimeout(resolve, this._getRetryDelay(retryCount)));

      // connection retry
      OMT_Utils.stylizedLog('OMT_Connect: retry');
      this._logError(error);
      const authfailed = this._checkIfAuthFailed(error);
      const retrySuccess = await this._connectToGB(authfailed, retryCount + 1, maxRetries);
      return retrySuccess;
    });

    // set game-backend profile data once connected
    if (connected && !this._connected) {
      this._connected = true;
      const gbUserProfileData = await this._getGBUserProfile();
      OMT.envData.setGBUserProfileData(gbUserProfileData);
      this._errorCache.clearAllData();
      this.signals.onGBConnected.dispatch();
    }

    return connected;
  }

  /**
   * check if auth failed. NOT_AUTHORIZED && NOT_FOUND are for compatibility with GBC 2.X.X
   * @param {Object} error
   * @return {boolean}
   */
  _checkIfAuthFailed(error) {
    if (!error || !error.code) return true;
    return error.code === ERROR_CODE.INVALID_PARAMETER || error.code === ERROR_CODE.WRONG_CREDENTIALS || error.code === ERROR_CODE.NOT_AUTHORIZED || error.code === ERROR_CODE.NOT_FOUND;
  }

  /**
   * log the error, but we only want to do this once per connection cycle
   * @param {Error} error
   */
  _logError(error) {
    const cacheKey = `error-${(error.code || error.message || 'unknown').toString()}`;
    if (!this._errorCache.hasKey(cacheKey)) {
      const naString = 'N/A';
      this._errorCache.setData(cacheKey, { triggered: true });
      let errorString = `OMT_Connect Error: { code: ${error.code || naString}, message: ${error.message || naString}`;
      if (error.parentError) errorString += `, parent-code: ${error.parentError.code || naString}, parent-message: ${error.parentError.message || naString}`;
      errorString += ' }';
      G.Utils.SentryLog.logError(errorString, {}, false);
      console.error(errorString);
    }
  }

  /**
   * throttled reconnect to game-backend
   */
  async _retryGBConnect() {
    this._connected = await this._connectToGB(false, 0, 0);
  }

  /**
   * set a listener for the game-backend closed / lose connection
   */
  _setGBConnectionClosedListener() {
    // with GBC 3.0.0+ this should not be needed anymore. Left commented out in-case.
    /*
    GBC_closeEvent.on((code, reason) => {
      this._connected = false;
      OMT_Utils.stylizedLog('OMT_Connect: !!ERROR Lost connection', '#FFFF00');
      this._retryGBConnect();
    });
    */
  }

  /**
   * get the timestamp from the server once connected. If it cannot be retreived will return -1.
   * @returns {number}
   */
  async getServerTimestamp() {
    if (this._connected) {
      try {
        const serverTimestamp = await GBC_getServerTimestamp();
        return parseInt(serverTimestamp);
      } catch (error) {
        // console could not get timestamp from the server
      }
    }
    return -1;
  }
}
